import React from "react";
import {
  Box,
  Table as MUITable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Typography from "../typography";

/** Where a column goes in the below-md card layout. */
export type ColumnCardSlot =
  /** Headline row, no label. */
  | "primary"
  /** Grouped into a wrapping row with no labels — chips, badges. */
  | "inline"
  /** Footer row, no label — icon buttons. */
  | "actions"
  /** Dropped from the card. */
  | "hidden";

export interface ColumnProps<T> {
  header: string;
  align?: "left" | "right" | "center";
  width?: string;
  /** Defaults to a labelled `header / value` row. */
  card?: ColumnCardSlot;
  cell: (original: T) => React.ReactNode;
}

interface TableProps<T> {
  columns: ColumnProps<T>[];
  data: T[];
}

function Table<T>({ columns, data }: TableProps<T>) {
  const theme = useTheme();
  // A table with this many columns cannot be squeezed into a phone, so the
  // whole markup is swapped for a card per row rather than restyled.
  const isCompact = useMediaQuery(theme.breakpoints.down("md"));

  if (isCompact) {
    return <CardList columns={columns} data={data} />;
  }

  return (
    <TableContainer>
      <MUITable aria-label="custom table">
        <TableHead>
          <TableRow>
            {columns.map(({ header, align = "center", width }, i) => (
              <TableCell
                key={i}
                align={align}
                style={{
                  padding: `0 ${theme.spacing(1)}`,
                  background: "black",
                  color: "white",
                  ...(width ? { width } : {}),
                  ...(i + 1 !== columns.length
                    ? { borderRight: "solid 1px white" }
                    : {}),
                }}
              >
                {header}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {data.map((row, rowIndex) => (
            <TableRow key={rowIndex}>
              {columns.map((col, colIndex) => (
                <TableCell
                  key={colIndex}
                  align={col.align ?? "center"}
                  style={{
                    padding: `${theme.spacing(1.5)} ${theme.spacing(1)}`,
                    fontSize: "0.8em",
                  }}
                >
                  {col.cell(row)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </MUITable>
    </TableContainer>
  );
}

function CardList<T>({ columns, data }: TableProps<T>) {
  const theme = useTheme();

  const bySlot = (slot: ColumnCardSlot) =>
    columns.filter((col) => col.card === slot);

  const primary = bySlot("primary");
  const inline = bySlot("inline");
  const actions = bySlot("actions");
  const labelled = columns.filter((col) => !col.card);

  return (
    <Box
      sx={{
        padding: theme.spacing(2, 1.5),
        // Unbounded, a label and its value end up at opposite ends of a tablet.
        maxWidth: 720,
        margin: "0 auto",
      }}
    >
      {data.map((row, rowIndex) => (
        <Box
          key={rowIndex}
          sx={{
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            padding: 2,
            marginBottom: 1.5,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          {primary.length > 0 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                fontWeight: 600,
              }}
            >
              {primary.map((col, i) => (
                <Box key={i} sx={{ minWidth: 0 }}>
                  {col.cell(row)}
                </Box>
              ))}
            </Box>
          )}

          {labelled.map((col, i) => (
            <Box
              key={i}
              sx={{
                display: "flex",
                alignItems: "baseline",
                justifyContent: "space-between",
                gap: 2,
                marginTop: 1,
                fontSize: "0.9rem",
              }}
            >
              <Typography
                variant="caption"
                style={{ color: theme.palette.text.secondary }}
              >
                {col.header}
              </Typography>
              <Box sx={{ minWidth: 0, textAlign: "right" }}>
                {col.cell(row)}
              </Box>
            </Box>
          ))}

          {inline.length > 0 && (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 1,
                marginTop: 1.5,
              }}
            >
              {inline.map((col, i) => (
                <Box key={i}>{col.cell(row)}</Box>
              ))}
            </Box>
          )}

          {actions.length > 0 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: 1,
                marginTop: 1.5,
                paddingTop: 1.5,
                borderTop: `1px solid ${theme.palette.divider}`,
              }}
            >
              {actions.map((col, i) => (
                <Box key={i}>{col.cell(row)}</Box>
              ))}
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );
}

export default Table;
